import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  MeasuringStrategy,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  faAdd,
  faBracketsCurly,
  faCog,
  faPlus,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { twJoin } from "tailwind-merge";

import {
  ConfigSection,
  SchemaTab,
  useAgentVersionConfigPanel,
} from "src/agents/AgentVersionPage/AgentVersionConfigPanelContext";
import { useSideDrawer } from "src/agents/base/SideDrawer";
import { AgentVersionConfigurationInput } from "src/agents/types";
import { PropertyValue, Schema } from "src/api/flowTypes";
import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { Input } from "src/base-components/Input";
import { ReorderHandle } from "src/base-components/ReorderHandle";
import { Switch } from "src/base-components/Switch";
import { Tabs } from "src/base-components/Tabs";
import { SCHEMA_TYPE_ICONS } from "src/base-components/TypeIcons";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { JsonSchemaEmptyState } from "src/jsonSchema/editor/JsonSchemaEmptyState";
import {
  isArrayOfPrimitive,
  isObject,
  isObjectWithProperties,
  isPrimitive,
  isUnionOfPrimitivesOnly,
  JsonSchema,
  JsonSchemaNode,
} from "src/jsonSchema/types";
import { JSONSchemaRenderer } from "src/jsonSchema/viewer";
import { createTitleOnlyHeaderRenderer } from "src/jsonSchema/viewer/JSONSchemaRenderer";
import { SchemaOptions } from "src/router/SearchParams";
import { SchemaEnumErrorStatus } from "src/schema/SchemaEnumRow";
import { SchemaEnumRows } from "src/schema/SchemaEnumRows";
import {
  PropertyConverter,
  PropertyUI,
  SchemaConverter,
  SchemaUI,
} from "src/schema/schemaMappingUtils";
import { useOpenOnHover } from "src/utils/useOpenOnHover";
import { useScrollIntoView } from "src/utils/useScrollIntoView";

// ============================================================================
// Types
// ============================================================================

// Agent-specific property UI with description
export type AgentSchemaPropertyUI = PropertyUI & {
  description?: string;
};

// Agent-specific schema UI
export type AgentSchemaUI = SchemaUI & {
  properties: AgentSchemaPropertyUI[];
};

// Backend schema with description
type AgentSchemaBE = Schema & {
  properties: Record<string, PropertyValue & { description?: string }>;
};

// ============================================================================
// Type Options
// ============================================================================

const AGENT_SCHEMA_TYPE_OPTIONS_BASE = [
  { key: "string", value: "string", icon: SCHEMA_TYPE_ICONS.string },
  { key: "number", value: "number", icon: SCHEMA_TYPE_ICONS.number },
  { key: "boolean", value: "boolean", icon: SCHEMA_TYPE_ICONS.boolean },
  { key: "integer", value: "integer", icon: SCHEMA_TYPE_ICONS.integer },
  { key: "date", value: "date", icon: SCHEMA_TYPE_ICONS.date },
  { key: "datetime", value: "datetime", icon: SCHEMA_TYPE_ICONS.datetime },
  { key: "array", value: "array", icon: SCHEMA_TYPE_ICONS.array },
  { key: "object", value: "object", icon: SCHEMA_TYPE_ICONS.object },
  { key: "enum", value: "enum", icon: SCHEMA_TYPE_ICONS.enum },
];

// file[] is an input-only type displayed as "File(s)"
const AGENT_INPUT_SCHEMA_TYPE_OPTIONS = [
  ...AGENT_SCHEMA_TYPE_OPTIONS_BASE,
  { key: "file[]", value: "file(s)", icon: SCHEMA_TYPE_ICONS["file[]"] },
];

const AGENT_OUTPUT_SCHEMA_TYPE_OPTIONS = AGENT_SCHEMA_TYPE_OPTIONS_BASE;

/**
 * Creates type options from $defs in the schema.
 * These appear in the type dropdown as custom reference types.
 */
const createTypeOptionsFromDefs = (schema?: JsonSchema) => {
  if (!schema?.$defs) {
    return [];
  }

  return Object.keys(schema.$defs).map((defName) => ({
    key: `#/$defs/${defName}`,
    value: `${defName}`, // Display name in dropdown
    icon: SCHEMA_TYPE_ICONS.object, // Use object icon for custom types
  }));
};

// ============================================================================
// Conversion Functions
// ============================================================================

const DEFAULT_SCHEMA: AgentSchemaBE = {
  $schema: "https://json-schema.org/draft/2020-12/schema",
  type: "object",
  properties: {},
  required: [],
  order: [],
  sensitive: [],
};

const EMPTY_UI_SCHEMA: AgentSchemaUI = {
  $schema: DEFAULT_SCHEMA.$schema,
  type: "object" as const,
  properties: [],
};

// Convert backend JSON schema to UI-friendly format
export const convertAgentSchemaBEtoUI = (
  schemaBE: AgentSchemaBE | Schema | null | undefined,
): AgentSchemaUI | null => {
  if (!schemaBE || !schemaBE.properties) return null;

  // Use the shared SchemaConverter for the heavy lifting
  const schemaUI = SchemaConverter.beToUI(
    schemaBE as Schema,
    SchemaOptions.Output,
  );

  // Add agent-specific fields (description)
  return {
    ...schemaUI,
    properties: schemaUI.properties.map((property) => ({
      ...property,
      description:
        (
          schemaBE.properties as Record<
            string,
            PropertyValue & { description?: string }
          >
        )[property.fieldName]?.description ?? "",
    })),
  };
};

// Convert UI format back to backend JSON schema
export const convertAgentSchemaUIToBE = (
  schemaUI: AgentSchemaUI,
): AgentSchemaBE => {
  // Use the shared SchemaConverter for the heavy lifting
  const schemaBE = SchemaConverter.uiToBE(schemaUI, SchemaOptions.Output);

  // Create mapping for agent-specific fields
  const descriptionMapping = Object.fromEntries(
    schemaUI.properties.map((property) => [
      property.fieldName,
      property.description,
    ]),
  );

  // Add description back to each property
  return {
    ...schemaBE,
    properties: Object.fromEntries(
      Object.entries(schemaBE.properties).map(([fieldName, property]) => [
        fieldName,
        {
          ...property,
          description: descriptionMapping[fieldName],
        },
      ]),
    ),
  };
};

// Factory for new empty properties
export const getAgentNewProperty = (
  existingProperties: AgentSchemaPropertyUI[],
): AgentSchemaPropertyUI => {
  // Generate unique field name
  const existingNames = new Set(existingProperties.map((p) => p.fieldName));
  let counter = existingProperties.length + 1;
  while (existingNames.has(`field_${counter}`)) {
    counter++;
  }

  return {
    fieldName: `field_${counter}`,
    type: ["string", false], // PropertyTypeUI format: [type, nullable]
    required: true,
    sensitive: false,
    description: "",
  };
};

// ============================================================================
// Main Component: AgentSchemaEditor
// ============================================================================

type AgentSchemaEditorProps = {
  immutable: boolean;
};

export const AgentSchemaEditor: React.FC<AgentSchemaEditorProps> = ({
  immutable,
}) => {
  const { schemaTab, setSchemaTab, registerScrollFn, jsonSchemaDrawerEditing } =
    useAgentVersionConfigPanel();
  const { isOpen: isJsonDrawerOpen } = useSideDrawer("jsonSchema");

  // Scroll hook for tabs - also used as the Schema section scroll target
  const { ref: tabsRef, scrollIntoView: scrollTabs } = useScrollIntoView();

  // Register tabs scroll as the Schema section scroll target
  // This ensures both accordion expand and pill clicks scroll to the same position
  useEffect(() => {
    registerScrollFn(ConfigSection.Schema, scrollTabs);
  }, [registerScrollFn, scrollTabs]);

  // Disable tab switching when JSON drawer is open
  const tabsDisabled = isJsonDrawerOpen;

  return (
    <div ref={tabsRef}>
      <p className="mb-4 font-inter-normal-12px text-gray-500">
        Configure the input and output fields for this Agent version
      </p>

      <div className="rounded-lg bg-gray-50 px-4 py-3">
        <Tabs
          panelsClassName="pt-5"
          selectedKey={schemaTab}
          tabListClassName="border-b border-gray-200"
          tabs={[
            {
              key: "input",
              label:
                tabsDisabled && schemaTab !== "input" ? (
                  <Tooltip
                    body="Close the JSON schema editor to switch tabs"
                    asChild
                  >
                    <span>Input</span>
                  </Tooltip>
                ) : (
                  "Input"
                ),
              disabled: tabsDisabled && schemaTab !== "input",
              content: (
                <InputSchemaEditor
                  immutable={immutable || jsonSchemaDrawerEditing === "input"}
                />
              ),
            },
            {
              key: "output",
              label:
                tabsDisabled && schemaTab !== "output" ? (
                  <Tooltip
                    body="Close the JSON schema editor to switch tabs"
                    asChild
                  >
                    <span>Output</span>
                  </Tooltip>
                ) : (
                  "Output"
                ),
              disabled: tabsDisabled && schemaTab !== "output",
              content: (
                <OutputSchemaEditor
                  immutable={immutable || jsonSchemaDrawerEditing === "output"}
                />
              ),
            },
          ]}
          unmountPanels={false}
          onChange={(key) => {
            setSchemaTab(key as SchemaTab);
            scrollTabs();
          }}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Schema Properties Editor
// ============================================================================

type SchemaPropertiesFormValues = {
  properties: AgentSchemaPropertyUI[];
};

type SchemaPropertiesEditorProps = {
  initialProperties: AgentSchemaPropertyUI[];
  immutable: boolean;
  onChange: (properties: AgentSchemaPropertyUI[]) => void;
  dataLocPrefix: "input" | "output";
  schemaDirection: "input" | "output";
  schema?: JsonSchema | null; // Full schema with $defs for type dropdown
};

const SchemaPropertiesEditor: React.FC<SchemaPropertiesEditorProps> = ({
  initialProperties,
  immutable,
  onChange,
  dataLocPrefix,
  schemaDirection,
  schema,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 50);
  }, []);

  // Create extended type options with $defs
  const baseOptions =
    schemaDirection === "input"
      ? AGENT_INPUT_SCHEMA_TYPE_OPTIONS
      : AGENT_OUTPUT_SCHEMA_TYPE_OPTIONS;
  const typeOptions = useMemo(() => {
    return [...baseOptions, ...createTypeOptionsFromDefs(schema ?? undefined)];
  }, [baseOptions, schema]);

  // Track when change originated from form's own onChange (to skip unnecessary resets)
  const isInternalChangeRef = useRef(false);

  const formMethods = useForm<SchemaPropertiesFormValues>({
    defaultValues: {
      properties: initialProperties,
    },
    mode: "onChange",
  });

  const { fields, append, remove, move } = useFieldArray({
    control: formMethods.control,
    name: "properties",
  });

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragCancel = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle drag-and-drop reordering
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setIsDragging(false);
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = fields.findIndex((f) => f.id === active.id);
        const newIndex = fields.findIndex((f) => f.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          move(oldIndex, newIndex);
        }
      }
    },
    [fields, move],
  );

  // Reset form when initialProperties changes externally (e.g., JSON editor saves)
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return; // Skip reset - change came from form itself
    }
    formMethods.reset({ properties: initialProperties });
  }, [initialProperties, formMethods]);

  const handleAddField = useCallback(() => {
    const properties = formMethods.getValues("properties");
    const newProperty = getAgentNewProperty(properties);
    append(newProperty);
    scrollToBottom();
  }, [append, formMethods, scrollToBottom]);

  // Subscribe to form changes and propagate valid changes to parent
  useEffect(
    () =>
      formMethods.subscribe({
        formState: { values: true },
        callback: async ({ values: _ }) => {
          const isValid = await formMethods.trigger();
          if (!isValid) return;

          const currentValues = formMethods.getValues();
          isInternalChangeRef.current = true; // Mark as internal change
          onChange(currentValues.properties);
        },
      }),
    [formMethods, onChange],
  );

  return (
    <FormProvider {...formMethods}>
      <div className={twJoin(isDragging && "cursor-grabbing")}>
        {/* Its configured this way to not distort elements of different heights while dragging */}
        <DndContext
          measuring={{
            droppable: {
              strategy: MeasuringStrategy.Always,
            },
          }}
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
        >
          <SortableContext
            disabled={immutable}
            items={fields.map((f) => f.id)}
            strategy={verticalListSortingStrategy}
          >
            {fields.length > 0 && <SchemaFieldHeader />}
            {fields.map((field, index) => (
              <SchemaFieldRow
                key={field.id}
                id={field.id}
                immutable={immutable}
                index={index}
                typeOptions={typeOptions}
                onDelete={() => remove(index)}
              />
            ))}
          </SortableContext>
          <DragOverlay dropAnimation={null} />
        </DndContext>
      </div>
      <Button
        dataLoc={`add-${dataLocPrefix}-schema-field`}
        disabled={immutable}
        iconLeft={faPlus}
        size="sm"
        variant="secondary"
        onClick={handleAddField}
      >
        Add field
      </Button>
      <div ref={bottomRef} />
    </FormProvider>
  );
};

// ============================================================================
// Input Schema Editor
// ============================================================================

type InputSchemaEditorProps = {
  immutable: boolean;
};

const InputSchemaEditor: React.FC<InputSchemaEditorProps> = ({ immutable }) => {
  const { open: openJsonEditor } = useSideDrawer("jsonSchema");
  const { control, setValue } =
    useFormContext<AgentVersionConfigurationInput>();
  const formSchema = useWatch({
    name: "input_schema",
    control,
  }) as Schema | null;

  // Convert form schema to UI format, preserving $defs and other fields
  const currentSchemaUI = useMemo(
    () => convertAgentSchemaBEtoUI(formSchema),
    [formSchema],
  );
  const currentProperties = useMemo(
    () => currentSchemaUI?.properties ?? [],
    [currentSchemaUI],
  );

  const isEmpty = currentProperties.length === 0;

  const handleChange = useCallback(
    (properties: AgentSchemaPropertyUI[]) => {
      if (properties.length === 0) {
        setValue("input_schema", null);
        return;
      }
      // Preserve $defs and other schema fields from the current schema
      const backendSchema = convertAgentSchemaUIToBE({
        ...(currentSchemaUI ?? DEFAULT_SCHEMA),
        properties,
      } as AgentSchemaUI);
      setValue("input_schema", backendSchema);
    },
    [setValue, currentSchemaUI],
  );

  const handleOpenJsonEditor = useCallback(() => {
    openJsonEditor({ schemaType: "input", readOnly: immutable });
  }, [openJsonEditor, immutable]);

  const handleAddField = useCallback(() => {
    const newProperty = getAgentNewProperty(currentProperties);
    handleChange([...currentProperties, newProperty]);
  }, [currentProperties, handleChange]);

  return (
    <div>
      {isEmpty ? (
        <InputSchemaEmptyState
          immutable={immutable}
          onAddField={handleAddField}
          onOpenRawJsonView={handleOpenJsonEditor}
        />
      ) : (
        <>
          {/* Header with JSON view button */}
          <div className="mb-4 flex items-start justify-between">
            <h3 className="font-inter-semibold-13px text-gray-800">Fields</h3>
            <Button
              dataLoc="open-input-json-schema-editor"
              size="sm"
              variant="secondary"
              onClick={handleOpenJsonEditor}
            >
              JSON view
            </Button>
          </div>
          <SchemaPropertiesEditor
            dataLocPrefix="input"
            immutable={immutable}
            initialProperties={currentProperties}
            schema={formSchema}
            schemaDirection="input"
            onChange={handleChange}
          />
        </>
      )}
    </div>
  );
};

// ============================================================================
// Output Schema Editor
// ============================================================================

type OutputSchemaEditorProps = {
  immutable: boolean;
};

const OutputSchemaEditor: React.FC<OutputSchemaEditorProps> = ({
  immutable,
}) => {
  const { open: openJsonEditor } = useSideDrawer("jsonSchema");
  const { control, setValue } =
    useFormContext<AgentVersionConfigurationInput>();
  const formSchema = useWatch({
    name: "output_schema",
    control,
  }) as Schema | null;

  // Convert form schema to UI format, preserving $defs and other fields
  const currentSchemaUI = useMemo(
    () => convertAgentSchemaBEtoUI(formSchema),
    [formSchema],
  );
  const currentProperties = useMemo(
    () => currentSchemaUI?.properties ?? [],
    [currentSchemaUI],
  );

  const isEmpty = currentProperties.length === 0;

  // Handle property changes from SchemaPropertiesEditor
  const handlePropertiesChange = useCallback(
    (properties: AgentSchemaPropertyUI[]) => {
      if (properties.length === 0) {
        setValue("output_schema", null);
        return;
      }

      // Preserve $defs and other schema fields from the current schema
      const backendSchema = convertAgentSchemaUIToBE({
        ...(currentSchemaUI ?? EMPTY_UI_SCHEMA),
        properties,
      });
      setValue("output_schema", backendSchema);
    },
    [setValue, currentSchemaUI],
  );

  const handleOpenJsonEditor = useCallback(() => {
    openJsonEditor({ schemaType: "output", readOnly: immutable });
  }, [openJsonEditor, immutable]);

  const handleAddField = useCallback(() => {
    const newProperty = getAgentNewProperty(currentProperties);
    handlePropertiesChange([...currentProperties, newProperty]);
  }, [currentProperties, handlePropertiesChange]);

  return (
    <div>
      {isEmpty ? (
        <JsonSchemaEmptyState
          immutable={immutable}
          onAddField={handleAddField}
          onOpenRawJsonView={handleOpenJsonEditor}
        />
      ) : (
        <>
          {/* Header with JSON view button */}
          <div className="mb-4 flex items-start justify-between">
            <h3 className="font-inter-semibold-13px text-gray-800">Fields</h3>
            <Button
              dataLoc="open-json-schema-editor"
              size="sm"
              variant="secondary"
              onClick={handleOpenJsonEditor}
            >
              JSON view
            </Button>
          </div>
          <SchemaPropertiesEditor
            dataLocPrefix="output"
            immutable={immutable}
            initialProperties={currentProperties}
            schema={formSchema}
            schemaDirection="output"
            onChange={handlePropertiesChange}
          />
        </>
      )}
    </div>
  );
};

// ============================================================================
// Schema Field Header
// ============================================================================

const SchemaFieldHeader: React.FC = () => (
  <div className="mb-2 flex items-center gap-2 font-inter-normal-12px text-gray-500">
    {/* Spacer for reorder handle */}
    <div className="w-7 flex-shrink-0" />
    <div className="flex flex-1 items-center">
      <div className="w-[60%]">Name</div>
      <div className="w-[40%]">Type</div>
    </div>
  </div>
);

// ============================================================================
// Schema Field Row Component
// ============================================================================

type SchemaFieldRowProps = {
  id: string;
  index: number;
  immutable: boolean;
  typeOptions: typeof AGENT_SCHEMA_TYPE_OPTIONS_BASE;
  onDelete: () => void;
};

const SchemaFieldRow: React.FC<SchemaFieldRowProps> = ({
  id,
  index,
  immutable,
  typeOptions,
  onDelete,
}) => {
  const hoverProps = useOpenOnHover<HTMLDivElement, HTMLDivElement>(100);
  const {
    control,
    formState: { errors },
  } = useFormContext<SchemaPropertiesFormValues>();

  // Sortable hook for drag-and-drop reordering
  const { listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const fieldError = errors.properties?.[index]?.fieldName;
  const propertyError = errors.properties?.[index];

  // Watch the property to build schema preview
  const propertyUI = useWatch({
    control,
    name: `properties.${index}`,
  }) as AgentSchemaPropertyUI;

  // Build schema node for preview
  const schemaNode = PropertyConverter.uiToBEProperty(
    propertyUI,
    SchemaOptions.Output,
  ) as JsonSchemaNode;

  const fullSchema = propertyUI._schema as JsonSchema;

  // Build preview schema with field name as property key
  const treePreviewSchema = useMemo(
    () => ({
      ...(fullSchema ?? {}),
      properties: {
        [propertyUI.fieldName]: schemaNode,
      },
    }),
    [fullSchema, propertyUI.fieldName, schemaNode],
  );

  // Determine if preview should be shown
  const showPreview =
    schemaNode &&
    !isPrimitive(schemaNode) &&
    !isArrayOfPrimitive(schemaNode) &&
    !isUnionOfPrimitivesOnly(schemaNode) &&
    !(isObject(schemaNode) && !isObjectWithProperties(schemaNode));

  return (
    <div
      ref={setNodeRef}
      className={twJoin(
        "mb-3 rounded bg-gray-50",
        isDragging && "relative z-10 cursor-grabbing",
      )}
      style={style}
    >
      {/* Main row */}
      <div className="flex items-center gap-2">
        <ReorderHandle
          immutable={immutable}
          isGrabbing={isDragging}
          listeners={listeners}
        />
        <div className="w-[60%]">
          <Controller
            control={control}
            name={`properties.${index}.fieldName`}
            render={({ field }) => (
              <Input
                disabled={immutable}
                errored={!!fieldError}
                placeholder="field_name"
                value={field.value}
                fullWidth
                onChange={field.onChange}
              />
            )}
            rules={{
              validate: {
                notEmpty: (value) =>
                  value?.trim() !== "" || "Field name cannot be empty",
                unique: (value, formValues) => {
                  const allFieldNames = formValues.properties.map(
                    (p) => p.fieldName,
                  );
                  const duplicateCount = allFieldNames.filter(
                    (name) => name === value,
                  ).length;
                  return duplicateCount <= 1 || "Field name must be unique";
                },
              },
            }}
          />
        </div>
        <div className="flex w-[40%] items-center justify-end gap-2">
          <Controller
            control={control}
            name={`properties.${index}`}
            render={({ field }) => {
              const primaryType = field.value.type?.[0] ?? "string";
              const currentNullable = field.value.type?.[1] ?? false;
              return (
                <FixedSimpleDropdown
                  buttonDataLoc={`schema-type-${index}`}
                  disabled={immutable}
                  elements={typeOptions}
                  placeholder="Select type"
                  selectedKey={primaryType}
                  onSelect={(key) => {
                    field.onChange({
                      ...field.value,
                      type: [key, currentNullable],
                      enum:
                        key === "enum" ? [{ value: '"value_1"' }] : undefined,
                    });
                  }}
                />
              );
            }}
          />
          <div className="flex items-center gap-1">
            {/* TODO: Re-enable once we use the sensitive part of the JSON schema.
               Right now it's not used for anything so we hide it to avoid confusion. */}
            {/* <Controller
              control={control}
              name={`properties.${index}.sensitive`}
              render={({ field }) => (
                <Tooltip
                  placement="top"
                  title={
                    field.value ? "Marked as sensitive" : "Mark as sensitive"
                  }
                  asChild
                >
                  <Icon
                    color={twJoin(
                      !immutable && "cursor-pointer",
                      field.value
                        ? "text-indigo-600"
                        : "text-gray-500 hover:text-gray-700",
                    )}
                    dataLoc={`schema-field-sensitive-${index}`}
                    icon={field.value ? faEyeSlashSolid : faEyeSlash}
                    size="sm"
                    onClick={() => !immutable && field.onChange(!field.value)}
                  />
                </Tooltip>
              )}
            /> */}
            {/* Settings Hover Popup */}
            <div className="relative">
              <div
                onMouseEnter={hoverProps.onMouseEnter}
                onMouseLeave={hoverProps.onMouseLeave}
                className={twJoin(!immutable && "cursor-pointer")}
              >
                <Icon
                  variant={hoverProps.isOpen ? "hover" : "default"}
                  dataLoc={`schema-field-advanced-trigger-${index}`}
                  icon={faCog}
                  size="xs"
                />
              </div>
              {hoverProps.isOpen && (
                <div
                  className="absolute -right-3 z-50 w-[9.5rem] rounded-lg bg-white px-3 py-4 font-inter-normal-13px text-gray-800 shadow-lg"
                  data-loc={`schema-field-advanced-popup-${index}`}
                  onMouseEnter={hoverProps.onMouseEnter}
                  onMouseLeave={hoverProps.onMouseLeave}
                >
                  <Controller
                    control={control}
                    name={`properties.${index}.required`}
                    render={({ field }) => (
                      <div className="mb-4 flex items-center justify-between">
                        <span>Required</span>
                        <Switch
                          dataLoc={`schema-field-required-${index}`}
                          disabled={immutable}
                          enabled={!!field.value}
                          onChange={field.onChange}
                        />
                      </div>
                    )}
                  />
                  <Controller
                    control={control}
                    name={`properties.${index}.type`}
                    render={({ field }) => {
                      const [primaryType, nullable] = field.value ?? [
                        "string",
                        false,
                      ];
                      const isNullable = nullable === "null";
                      return (
                        <div className="flex items-center justify-between">
                          <span>Nullable</span>
                          <Switch
                            dataLoc={`schema-field-nullable-${index}`}
                            disabled={immutable}
                            enabled={isNullable}
                            onChange={(checked) => {
                              field.onChange([
                                primaryType,
                                checked ? "null" : false,
                              ]);
                            }}
                          />
                        </div>
                      );
                    }}
                  />
                </div>
              )}
            </div>
            {/* Delete button */}
            {!immutable && (
              <IconButton
                dataLoc={`delete-field-${index}`}
                icon={faTrashAlt}
                size="xs"
                onClick={onDelete}
              />
            )}
          </div>
        </div>
      </div>

      {/* Field-level error hints */}
      {fieldError && <ErrorHint margin="none">{fieldError.message}</ErrorHint>}

      {/* Description field */}
      <Controller
        control={control}
        name={`properties.${index}.description`}
        render={({ field }) => (
          <div className="mt-2 ml-7">
            <textarea
              className="w-full rounded-md border border-gray-200 px-3 py-2 font-inter-normal-12px text-gray-800 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
              disabled={immutable}
              placeholder="Tell the Agent about this field..."
              rows={2}
              value={field.value || ""}
              onChange={field.onChange}
            />
          </div>
        )}
      />

      {/* Enum values editor - shown when type is "enum" */}
      <Controller
        control={control}
        name={`properties.${index}`}
        render={({ field }) => {
          const primaryType = field.value.type?.[0];
          if (primaryType !== "enum") return <></>;

          return (
            <SchemaEnumRows
              enumFormLocation={`properties.${index}.enum`}
              errorStatus={propertyError?.enum as SchemaEnumErrorStatus}
              immutable={immutable}
              withValidation={true}
            />
          );
        }}
      />

      {/* JSON Schema Preview for nested/composite types */}
      {showPreview && (
        <div className="mt-2 ml-1">
          <JSONSchemaRenderer
            displayOptions={{
              showTypeLabels: true,
              showPrimitiveTypeLabels: true,
              showDataPaths: false,
              maxDepth: 6,
              showRequired: true,
              customLabelAndTypeRenderer: createTitleOnlyHeaderRenderer(),
              autoExpandDepth: 3,
            }}
            schema={treePreviewSchema}
          />
        </div>
      )}
    </div>
  );
};

export type InputSchemaEmptyStateProps = {
  immutable: boolean;
  onAddField: () => void;
  onOpenRawJsonView: () => void;
};

const InputSchemaEmptyState: React.FC<InputSchemaEmptyStateProps> = ({
  immutable,
  onAddField,
  onOpenRawJsonView,
}) => {
  return (
    <EmptyState
      dataLoc="input-schema-empty-state"
      icon={faBracketsCurly}
      headline="Create input schema"
      description="Click below to start creating your input schema by adding a field or generate it from JSON."
      action={
        <div className="flex items-center gap-4">
          <div className="w-[120px]">
            <Button
              dataLoc="add-input-schema-field"
              disabled={immutable}
              iconLeft={faAdd}
              size="sm"
              variant="secondary"
              fullWidth
              onClick={onAddField}
            >
              Add field
            </Button>
          </div>
          <span className="font-inter-normal-12px text-gray-400">or</span>
          <div className="w-[120px]">
            <Button
              dataLoc="open-input-json-schema-editor"
              disabled={immutable}
              iconLeft={faBracketsCurly}
              size="sm"
              variant="secondary"
              fullWidth
              onClick={onOpenRawJsonView}
            >
              JSON schema
            </Button>
          </div>
        </div>
      }
    />
  );
};
